import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../context/AuthContext";
import { useCustomerWorkspace } from "../context/CustomerWorkspaceContext";
import { useRedditFeed } from "../context/RedditFeedContext";
import type { RedditPost } from "../context/RedditFeedContext";
import { fetchAgentsByCustomer } from "../services/agentService";
import { fetchRepliedPosts } from "../services/redditService";
import { AGENT_TYPE, pickAgentTypeFromApi } from "../constants/agentTypes";
import { RedditFeedList, formatGeneratedAt } from "../components/Reddit/RedditFeedList";

interface RedditPageProps {
  /**
   * When embedded inside the Content hub (SocialPostsPageV2), the hub already
   * supplies the page header, business pill, and card shell — so render only
   * the board. Standalone, render the full page chrome. Mirrors BlogsPage /
   * ConnectPage.
   */
  embedded?: boolean;
}

interface AgentOption {
  id: string;
  name: string;
}

/** "open" is the curated, brand-relevant board; "other" holds threads an
 *  explicit topic search found that this business can't usefully answer. */
type BoardTab = "open" | "other" | "replied";

const BOARD_TAB_LABELS: Record<BoardTab, string> = {
  open: "For Your Business",
  other: "Other Topics",
  replied: "Replied",
};

/** `Date.UTC` seconds → `YYYY-MM-DD`, for comparing against the date filter. */
const dayOf = (createdUtc: number): string =>
  new Date(createdUtc * 1000).toISOString().slice(0, 10);

/** "2026-08" → "August 2026". */
const monthLabel = (ym: string): string => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

/** "2026-08" → the month's first and last calendar day, as YYYY-MM-DD. */
const monthBounds = (ym: string): { start: string; end: string } => {
  const [y, m] = ym.split("-").map(Number);
  const lastDay = new Date(y, m, 0).getDate(); // day 0 of next month = last day of this one
  return { start: `${ym}-01`, end: `${ym}-${String(lastDay).padStart(2, "0")}` };
};

/** Month abbreviations for the picker grid, in calendar order. */
const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** How far back the year stepper allows. The feed has no data older than the
 *  product, so there is nothing to find beyond a few years. */
const YEARS_BACK = 3;

/**
 * Filter the board to a single calendar month.
 *
 * Year stepper on top, month grid underneath, rather than one flat list of
 * recent months: a flat list only works while "recent" is short, and gives no
 * way to reach a specific older month without scrolling past every month in
 * between. Picking a year first turns that into two obvious choices.
 *
 * Future months are disabled rather than hidden, so the grid keeps a stable
 * 3x4 shape and it is visible *why* they can't be picked. Selecting applies
 * immediately — with a single value there is nothing an Apply button would
 * batch.
 */
const MonthFilterDropdown: React.FC<{
  value: string | null;
  onChange: (value: string | null) => void;
}> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonthIdx = now.getMonth();

  const selectedYear = value ? Number(value.split("-")[0]) : null;
  const selectedMonthIdx = value ? Number(value.split("-")[1]) - 1 : null;

  // Which year the grid is showing. Opens on the selected month's year so
  // reopening the picker lands where the user left it, not on today.
  const [viewYear, setViewYear] = useState<number>(selectedYear ?? currentYear);
  useEffect(() => {
    if (isOpen) setViewYear(selectedYear ?? currentYear);
  }, [isOpen, selectedYear, currentYear]);

  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isOpen]);

  const canGoBack = viewYear > currentYear - YEARS_BACK;
  const canGoForward = viewYear < currentYear;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary ${
          value
            ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
            : "border-[var(--color-input-border)] bg-[var(--color-input-bg)] text-theme-fg hover:bg-theme-secondary/10"
        }`}
      >
        <svg className="h-4 w-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span>{value ? monthLabel(value) : "Filter by date"}</span>
        <svg
          className={`h-4 w-4 opacity-70 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 z-[100] mt-1.5 w-64 rounded-xl border border-theme-border bg-theme-surface p-3 shadow-xl">
          {/* Year stepper */}
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() => canGoBack && setViewYear((y) => y - 1)}
              disabled={!canGoBack}
              aria-label="Previous year"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-theme-fg-muted transition-colors hover:bg-theme-secondary/10 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-semibold text-theme-fg">{viewYear}</span>
            <button
              type="button"
              onClick={() => canGoForward && setViewYear((y) => y + 1)}
              disabled={!canGoForward}
              aria-label="Next year"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-theme-fg-muted transition-colors hover:bg-theme-secondary/10 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-3 gap-1">
            {MONTH_ABBR.map((label, idx) => {
              const isFuture =
                viewYear > currentYear ||
                (viewYear === currentYear && idx > currentMonthIdx);
              const isSelected = selectedYear === viewYear && selectedMonthIdx === idx;
              return (
                <button
                  key={label}
                  type="button"
                  disabled={isFuture}
                  onClick={() => {
                    onChange(`${viewYear}-${String(idx + 1).padStart(2, "0")}`);
                    setIsOpen(false);
                  }}
                  className={`rounded-lg px-2 py-1.5 text-sm transition-colors ${
                    isSelected
                      ? "bg-brand-primary font-medium text-white"
                      : isFuture
                      ? "cursor-not-allowed text-theme-fg-muted/30"
                      : "text-theme-fg hover:bg-theme-secondary/10"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {value && (
            <div className="mt-3 border-t border-theme-border pt-2">
              <button
                type="button"
                onClick={() => {
                  onChange(null);
                  setIsOpen(false);
                }}
                className="flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-theme-fg-muted transition-colors hover:bg-theme-secondary/10"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear filter
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** One "N Label" tile in the stats row — icon in a tinted circle, count, caption. */
const StatTile: React.FC<{
  icon: React.ReactNode;
  tint: "red" | "green";
  count: number;
  label: string;
  caption: string;
}> = ({ icon, tint, count, label, caption }) => (
  <div className="flex items-center gap-3 rounded-xl border border-theme-border bg-theme-surface p-4">
    <span
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
        tint === "red" ? "bg-red-500/10 text-red-500" : "bg-green-500/10 text-green-600"
      }`}
    >
      {icon}
    </span>
    <div>
      <p className="text-xl font-bold text-theme-fg">{count}</p>
      <p className="text-xs text-theme-fg-muted">
        {label} <span className="text-theme-fg-muted/70">· {caption}</span>
      </p>
    </div>
  </div>
);

const RedditPageBody: React.FC = () => {
  const { idToken } = useAuth();
  const { selectedCustomerId } = useCustomerWorkspace();
  const redditFeed = useRedditFeed();

  // The provider's "ready" toast (used by the chat modal's + menu entry
  // point, where a build can finish while the user is elsewhere) only fires
  // when the panel is *not* already open — mirrors NewChatWindow's own
  // effect. This tab IS the panel for as long as it's on screen, so mark it
  // open on mount and clear on unmount.
  useEffect(() => {
    redditFeed.setPanelOpen(true);
    return () => redditFeed.setPanelOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------
  // Agent picker. The board is keyed per customer *and* agent (distinct
  // agents represent distinct brands), but the Content hub only carries a
  // customer — so this tab needs its own picker, scoped to content_creator
  // agents the same way the chat "+" menu button is gated.
  // ---------------------------------------------------------------------
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const [agentId, setAgentId] = useState<string>("");

  // The reddit fetch is fired directly at the two points where the correct
  // (customerId, agentId) pairing is actually known — here, right after the
  // agent auto-select resolves, and in the picker's onChange below — rather
  // than from a second effect watching `agentId`. A separate effect raced:
  // on a customer switch, `selectedCustomerId` updates one render before
  // this effect's `setAgentId("")` takes effect, so an effect watching
  // `agentId` fires first with the *previous* customer's still-lingering
  // agent id — a genuine cross-customer request. That request's `inFlight`
  // lock in RedditFeedContext then blocks the correct request (fired once
  // the real agent resolves) from running at all, since nothing re-triggers
  // an effect a second time for values it already saw settle. Firing the
  // fetch inline, from the async callback that just resolved the correct
  // agent, makes the race impossible instead of managing it.
  useEffect(() => {
    setAgentId("");
    setAgents([]);
    if (!selectedCustomerId || !idToken) return;

    let cancelled = false;
    (async () => {
      setAgentsLoading(true);
      setAgentsError(null);
      try {
        const fetched = await fetchAgentsByCustomer(selectedCustomerId, idToken);
        if (cancelled) return;
        const list: AgentOption[] = (Array.isArray(fetched) ? fetched : [])
          .filter((raw: Record<string, unknown>) => {
            // Agents created before agent_type existed have no value; those
            // are content_creator by convention (the original agent type).
            const type = pickAgentTypeFromApi(raw) || AGENT_TYPE.CONTENT_CREATOR;
            return type === AGENT_TYPE.CONTENT_CREATOR;
          })
          .map((raw: Record<string, unknown>) => ({
            id: String((raw as any).aiagent_id ?? (raw as any).agent_id ?? ""),
            name: String((raw as any).agent_name ?? "Unnamed agent"),
          }))
          .filter((a) => a.id);
        setAgents(list);
        const chosen = list.length > 0 ? list[0].id : "";
        setAgentId(chosen);
        if (chosen) {
          redditFeed.ensureLoaded({ customerId: selectedCustomerId, agentId: chosen, idToken, origin: "content" });
        }
      } catch {
        if (!cancelled) setAgentsError("Couldn't load Reddit questions for this business.");
      } finally {
        if (!cancelled) setAgentsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // redditFeed.ensureLoaded is stable (see RedditFeedContext) — omitted
    // deliberately so a re-render doesn't re-trigger this fetch-on-mount effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId, idToken]);

  // The provider is agent-scoped and app-wide, so it may hold a different
  // agent's feed while this one is still loading — same guard NewChatWindow
  // uses before rendering its cards.
  const isThisAgentsFeed = redditFeed.feedAgentId === agentId;
  const openBoard = isThisAgentsFeed ? redditFeed.posts : [];

  // "Last checked" is relative, so it goes stale just sitting on screen —
  // re-render it each minute. Cheap, and matches the granularity the label
  // actually shows (nothing finer than minutes).
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const lastCheckedLabel = useMemo(
    () => (isThisAgentsFeed ? formatGeneratedAt(redditFeed.generatedAt) : null),
    // nowTick is the whole point of the dependency — it forces the relative
    // label to recompute even though generatedAt itself hasn't changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isThisAgentsFeed, redditFeed.generatedAt, nowTick]
  );

  // ---------------------------------------------------------------------
  // Filters — subreddit (category) and date range. Date range also drives
  // the Replied tab's server-side query; on the Open board (≤60 cards, held
  // in memory already) it's a client-side filter.
  // ---------------------------------------------------------------------
  const [boardTab, setBoardTab] = useState<BoardTab>("open");
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  // Optional topics for the next run. Comma-separated because the backend
  // caps at two searches anyway (Reddit's rate limit), so a full tag-input
  // widget would be UI for a list that can only ever hold two items.
  const [searchInput, setSearchInput] = useState("");
  const runSearch = useCallback(() => {
    const terms = searchInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    // Empty input runs the ordinary brand-derived build — same button, no
    // separate mode to discover.
    redditFeed.refresh(terms.length ? terms : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);
  const appliedRange = useMemo(
    () => (selectedMonth ? monthBounds(selectedMonth) : { start: "", end: "" }),
    [selectedMonth]
  );

  /** Same agent guard as the board: the provider is app-wide. */
  const otherTopics = isThisAgentsFeed ? redditFeed.otherTopics : [];

  const inAppliedMonth = useCallback(
    (createdUtc?: number) => {
      // A date filter must not silently hide cards the backend couldn't date
      // (the RSS fallback path returns null created_utc) — undated cards stay
      // visible rather than vanishing under a filter the user can't explain.
      if (!appliedRange.start && !appliedRange.end) return true;
      if (!createdUtc) return true;
      const day = dayOf(createdUtc);
      if (appliedRange.start && day < appliedRange.start) return false;
      if (appliedRange.end && day > appliedRange.end) return false;
      return true;
    },
    [appliedRange]
  );

  const filteredOtherTopics = useMemo(
    () => otherTopics.filter((p) => inAppliedMonth(p.created_utc)),
    [otherTopics, inAppliedMonth]
  );

  const filteredOpenBoard = useMemo(() => {
    return openBoard.filter((p) => {
      // A date filter must not silently hide cards the backend couldn't date
      // (the RSS fallback path returns null created_utc) — undated cards stay
      // visible rather than vanishing under a filter the user can't explain.
      if ((appliedRange.start || appliedRange.end) && p.created_utc) {
        const day = dayOf(p.created_utc);
        if (appliedRange.start && day < appliedRange.start) return false;
        if (appliedRange.end && day > appliedRange.end) return false;
      }
      return true;
    });
  }, [openBoard, appliedRange]);

  // ---------------------------------------------------------------------
  // Replied tab — reads RedditRepliedPostsTable via /reddit/replied, not the
  // live board (a replied thread is deliberately dropped from the board's
  // storage once archived — see reddit_replied.py).
  // ---------------------------------------------------------------------
  const [repliedPosts, setRepliedPosts] = useState<RedditPost[]>([]);
  const [repliedLoading, setRepliedLoading] = useState(false);
  const [repliedError, setRepliedError] = useState<string | null>(null);
  const [repliedCursor, setRepliedCursor] = useState<string | null>(null);

  const loadReplied = useCallback(
    async (append: boolean) => {
      if (!selectedCustomerId || !agentId || !idToken) return;
      setRepliedLoading(true);
      setRepliedError(null);
      try {
        const result = await fetchRepliedPosts({
          customerId: selectedCustomerId,
          agentId,
          idToken,
          startDate: appliedRange.start || undefined,
          endDate: appliedRange.end || undefined,
          cursor: append ? repliedCursor : null,
        });
        setRepliedPosts((prev) => (append ? [...prev, ...result.posts] : result.posts));
        setRepliedCursor(result.nextCursor);
      } catch (e: any) {
        setRepliedError(e?.message || "Couldn't load replied posts.");
      } finally {
        setRepliedLoading(false);
      }
    },
    // repliedCursor deliberately excluded — it's read at call time via the
    // `append` branch, not something a *change* to it should re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCustomerId, agentId, idToken, appliedRange]
  );

  useEffect(() => {
    if (boardTab === "replied" && selectedCustomerId && agentId && idToken) {
      void loadReplied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardTab, selectedCustomerId, agentId, idToken, appliedRange]);

  const repliedAnsweredUrls = useMemo(
    () => new Set(repliedPosts.map((p) => p.url)),
    [repliedPosts]
  );

  if (!selectedCustomerId) {
    return (
      <p className="py-8 text-center text-sm text-theme-fg-muted">
        Select a business profile to see its Reddit feed.
      </p>
    );
  }

  if (agentsLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-sm text-theme-fg-muted">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-theme-border border-t-brand-primary" />
        Loading Reddit questions…
      </div>
    );
  }

  if (agentsError) {
    return <p className="py-8 text-center text-sm text-red-600">{agentsError}</p>;
  }

  if (agents.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-theme-fg-muted">
        No content agent set up for this business yet — create one from the
        sidebar to start getting Reddit questions.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Board switcher — which board am I looking at. Kept on its own row,
          set apart with a bottom border, so it doesn't blend into the filter
          controls below it: switching boards and refining within one board
          are different kinds of actions. */}
      <div className="flex flex-wrap items-center gap-3 border-b border-theme-border pb-4">
        <div
          role="tablist"
          aria-label="Reddit board"
          className="inline-flex items-center gap-1 rounded-lg border border-theme-border bg-theme-surface p-1"
        >
          {(["open", "other", "replied"] as BoardTab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={boardTab === tab}
              onClick={() => setBoardTab(tab)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                boardTab === tab
                  ? "bg-brand-primary text-white"
                  : "text-theme-fg-muted hover:bg-theme-secondary/10 hover:text-theme-fg"
              }`}
            >
              {BOARD_TAB_LABELS[tab]}
              {tab === "other" && otherTopics.length > 0 && (
                <span className="ml-1.5 rounded-full bg-theme-fg/10 px-1.5 py-0.5 text-[10px] font-semibold">
                  {otherTopics.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Overview stats — visible regardless of which board tab is active,
          same as the tab switcher above. "Total Questions" counts the Open
          board (respects the subreddit/month filters below it); "Replied
          Questions" is the full answered_urls set already held by the
          provider (see RedditFeedContext), which has no per-item date, so
          this one is deliberately labeled "all time" rather than faked as
          month-scoped. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <StatTile
          tint="red"
          count={filteredOpenBoard.length}
          label="Total questions"
          caption={selectedMonth ? monthLabel(selectedMonth) : "all time"}
          icon={
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatTile
          tint="green"
          count={redditFeed.answeredUrls.size}
          label="Replied questions"
          caption="all time"
          icon={
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          }
        />
      </div>

      {/* Filters + fetch for whichever board is selected above. */}
      <div className="flex flex-wrap items-center gap-3">
        <MonthFilterDropdown value={selectedMonth} onChange={setSelectedMonth} />

        {/* Set apart with its own tinted, bordered strip: everything in here
            goes out and does something (a 60-100s search) rather than
            filtering what's already on screen. The topic input lives inside
            the strip, immediately left of the button it feeds — next to the
            date filter it read as a third filter, when it actually changes
            what the *next search* looks for. */}
        <div className="ml-auto flex items-center gap-3 rounded-lg border border-brand-primary/40 bg-brand-primary/5 px-3 py-2">
          <span className="hidden max-w-[250px] flex-col leading-tight lg:flex">
            {/* Amber once the server flags the board as past its freshness
                window — the only remaining signal that it's old, now that the
                separate "showing posts from…" strip is gone (it duplicated
                both this timestamp and the button beside it). */}
            <span
              className={`text-[11px] font-medium ${
                isThisAgentsFeed && redditFeed.stale && lastCheckedLabel
                  ? "text-amber-600 dark:text-amber-500"
                  : "text-theme-fg"
              }`}
            >
              {lastCheckedLabel
                ? `Last checked ${lastCheckedLabel}`
                : "Not checked yet"}
            </span>
            {/* Says where the questions come from, why these ones, and that
                the board refills on its own: it's Reddit threads matched
                against the brand profile, not a generic feed. Without this the
                tab gives no clue what the selection is based on. */}
            <span className="text-[10px] leading-snug text-theme-fg-muted">
              Reddit questions matched to your brand — new ones every Monday
            </span>
          </span>

          <div className="relative">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !redditFeed.isRefreshing && !redditFeed.isLoading) {
                  runSearch();
                }
              }}
              placeholder="Any topic? (optional)"
              title="Look for threads about specific topics instead of the usual brand-derived ones. Separate two topics with a comma."
              className="w-44 rounded-lg border border-[var(--color-input-border)] bg-[var(--color-input-bg)] py-1.5 pl-3 pr-7 text-sm text-theme-fg placeholder:text-theme-fg-muted/70 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                aria-label="Clear topics"
                className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-theme-fg-muted transition-colors hover:bg-theme-secondary/20"
              >
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={runSearch}
            disabled={redditFeed.isLoading || redditFeed.isRefreshing}
            title="Check Reddit for new questions right now"
            className="flex shrink-0 items-center gap-1.5 rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brand-primary-hover disabled:opacity-50"
          >
            <svg
              className={`h-3.5 w-3.5 ${redditFeed.isRefreshing ? "animate-spin" : ""}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              {redditFeed.isRefreshing ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              )}
            </svg>
            <span>{redditFeed.isRefreshing ? "Checking Reddit…" : "Find New Questions"}</span>
          </button>
        </div>
      </div>

      {/* A build that added nothing is otherwise invisible — the board just
          sits there unchanged after a 60-100s wait, which reads as a failure.
          Say what actually happened. The 19-found/0-relevant case is the
          relevance gate doing its job on an off-brand topic, so it gets its
          own wording rather than a generic "no results". */}
      {boardTab === "open" &&
        isThisAgentsFeed &&
        !redditFeed.isLoading &&
        !redditFeed.isRefreshing &&
        redditFeed.lastRun &&
        // Belt and braces against a cross-profile leak: the provider is
        // app-wide, so only render an outcome stamped with the pair currently
        // on screen.
        redditFeed.lastRun.key === `${selectedCustomerId}|${agentId}` &&
        redditFeed.lastRun.newPosts === 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-[12px] text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
            <svg className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              {redditFeed.lastRun.candidatesFound > 0 ? (
                <>
                  Found {redditFeed.lastRun.candidatesFound} thread
                  {redditFeed.lastRun.candidatesFound === 1 ? "" : "s"}
                  {redditFeed.lastRun.searchTerms.length > 0 && (
                    <> for “{redditFeed.lastRun.searchTerms.join("”, “")}”</>
                  )}
                  , but none were a good fit for this business — so nothing was
                  added to the board.
                  {redditFeed.lastRun.searchTerms.length > 0 &&
                    " Try a topic closer to what your customers ask about."}
                </>
              ) : (
                <>
                  No new threads came back
                  {redditFeed.lastRun.searchTerms.length > 0 && (
                    <> for “{redditFeed.lastRun.searchTerms.join("”, “")}”</>
                  )}
                  . Reddit may have been rate-limiting us — worth trying again in
                  a few minutes.
                </>
              )}
            </span>
          </div>
        )}

      <div className="space-y-3">
        {boardTab === "other" ? (
          <RedditFeedList
            posts={filteredOtherTopics}
            isLoading={false}
            isRefreshing={isThisAgentsFeed ? redditFeed.isRefreshing : false}
            error={null}
            generatedAt={null}
            onRefresh={runSearch}
            regeneratingUrl={null}
            onRegenerate={() => {}}
            answeredUrls={new Set<string>()}
            onToggleAnswered={() => {}}
            readOnly
            emptyMessage="Nothing here yet. Search a topic above — anything that isn't a fit for your business lands here instead of the main board."
          />
        ) : boardTab === "open" ? (
          <RedditFeedList
            posts={filteredOpenBoard}
            isLoading={isThisAgentsFeed ? redditFeed.isLoading : true}
            isRefreshing={isThisAgentsFeed ? redditFeed.isRefreshing : false}
            error={isThisAgentsFeed ? redditFeed.error : null}
            generatedAt={isThisAgentsFeed ? redditFeed.generatedAt : null}
            onRefresh={runSearch}
            regeneratingUrl={redditFeed.regeneratingUrl}
            onRegenerate={(url, words) => void redditFeed.regenerateReply(url, words)}
            answeredUrls={redditFeed.answeredUrls}
            onToggleAnswered={(url) => void redditFeed.toggleAnswered(url)}
            pacingWarning={redditFeed.pacingWarning}
            emptyMessage={
              isThisAgentsFeed && redditFeed.generatedAt === null
                ? "No questions yet — click Find New Questions to check Reddit."
                : "No posts match this filter."
            }
          />
        ) : (
          <>
            <RedditFeedList
              posts={repliedPosts}
              isLoading={repliedLoading && repliedPosts.length === 0}
              isRefreshing={repliedLoading && repliedPosts.length > 0}
              error={repliedError}
              generatedAt={null}
              regeneratingUrl={null}
              onRegenerate={() => {}}
              answeredUrls={repliedAnsweredUrls}
              onToggleAnswered={() => {}}
              onRefresh={() => void loadReplied(false)}
              readOnly
              emptyMessage="No replied threads in this range yet."
              loadingMessage="Loading your replied threads…"
            />
            {repliedCursor && !repliedLoading && (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => void loadReplied(true)}
                  className="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-fg-muted transition-colors hover:bg-theme-secondary/10"
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const RedditPage: React.FC<RedditPageProps> = ({ embedded = false }) => {
  const { selectedCustomerName } = useCustomerWorkspace();

  if (embedded) {
    return <RedditPageBody />;
  }

  return (
    <div className="p-6">
      <div className="mx-auto max-w-[1130px] space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-theme-fg">Reddit</h1>
            {selectedCustomerName && (
              <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700 shadow-sm ring-1 ring-inset ring-green-600/20 transition-all hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:ring-green-500/20 dark:hover:bg-green-900/40">
                <span className="material-icons-outlined text-[18px] text-amber-600 opacity-90 dark:text-amber-500">
                  storefront
                </span>
                <span>{selectedCustomerName}</span>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-md border border-theme-border bg-theme-surface p-6 shadow-sm">
          <RedditPageBody />
        </div>
      </div>
    </div>
  );
};

export default RedditPage;
